import { motion, AnimatePresence } from "framer-motion";
import { DotGrid } from "./DotGrid";

const WAREHOUSE = { x: 310, y: 180 };
const ZONES = [
  { r: 60, color: "hsl(150 60% 45% / 0.15)", stroke: "hsl(150 60% 45% / 0.5)", label: "Zon 1", price: "49 kr" },
  { r: 120, color: "hsl(45 80% 55% / 0.12)", stroke: "hsl(45 80% 55% / 0.5)", label: "Zon 2", price: "99 kr" },
  { r: 180, color: "hsl(0 70% 60% / 0.10)", stroke: "hsl(0 70% 60% / 0.4)", label: "Zon 3", price: "149 kr" },
];

export const PRICING_DURATIONS = [2500, 2500, 2500, 2500, 2500, 2500];

export function PricingAnimation({
  currentStep,
  cycleKey,
}: {
  currentStep: number;
  cycleKey: number;
}) {
  const showWarehouse = currentStep >= 0;
  const showZones = currentStep >= 1;
  const showPrices = currentStep >= 2;
  const showTmsLink = currentStep >= 3;
  const showIncoming = currentStep >= 4;
  const showResponse = currentStep >= 5;

  return (
    <div className="relative">
      <svg
        viewBox="0 0 620 360"
        className="w-full rounded-xl border border-border/40 bg-white/70"
        style={{ aspectRatio: "620/360" }}
      >
        <DotGrid />

        {/* Concentric zones */}
        {showZones &&
          [...ZONES].reverse().map((zone, i) => (
            <motion.circle
              key={`zone-${i}`}
              cx={WAREHOUSE.x}
              cy={WAREHOUSE.y}
              r={zone.r}
              fill={zone.color}
              stroke={zone.stroke}
              strokeWidth={1.5}
              strokeDasharray="8 4"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: (ZONES.length - 1 - i) * 0.3 }}
            />
          ))}

        {/* Price labels */}
        {showPrices &&
          ZONES.map((zone, i) => {
            const angle = -Math.PI / 4 + i * 0.3;
            const labelX = WAREHOUSE.x + Math.cos(angle) * (zone.r - 15);
            const labelY = WAREHOUSE.y + Math.sin(angle) * (zone.r - 15);
            return (
              <motion.g
                key={`price-${i}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: i * 0.2 }}
              >
                <rect
                  x={labelX - 24}
                  y={labelY - 10}
                  width={48}
                  height={20}
                  rx={4}
                  fill="white"
                  stroke={zone.stroke}
                  strokeWidth={1}
                />
                <text
                  x={labelX}
                  y={labelY + 4}
                  textAnchor="middle"
                  fontSize="10"
                  fontFamily="Montserrat, system-ui"
                  fontWeight="600"
                  fill="hsl(0 0% 30%)"
                >
                  {zone.price}
                </text>
              </motion.g>
            );
          })}

        {/* Warehouse point */}
        {showWarehouse && (
          <motion.g key={`${cycleKey}-wh`}>
            <motion.circle
              cx={WAREHOUSE.x}
              cy={WAREHOUSE.y}
              r={10}
              fill="hsl(186 60% 40%)"
              stroke="hsl(186 60% 30%)"
              strokeWidth={2}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            />
            {/* Warehouse icon */}
            <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              <rect x={WAREHOUSE.x - 5} y={WAREHOUSE.y - 4} width={10} height={7} rx={1} fill="white" />
              <path
                d={`M ${WAREHOUSE.x - 6} ${WAREHOUSE.y - 3} L ${WAREHOUSE.x} ${WAREHOUSE.y - 7} L ${WAREHOUSE.x + 6} ${WAREHOUSE.y - 3}`}
                fill="white"
                stroke="white"
                strokeWidth={0.5}
              />
            </motion.g>
          </motion.g>
        )}

        {/* Delivery point for incoming */}
        {showIncoming && (
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <motion.circle cx={420} cy={130} r={6} fill="hsl(45 80% 55%)" stroke="hsl(45 80% 45%)" strokeWidth={1.5} />
            <motion.path
              d={`M 420,130 L ${WAREHOUSE.x},${WAREHOUSE.y}`}
              fill="none"
              stroke="hsl(45 80% 55% / 0.6)"
              strokeWidth={1.5}
              strokeDasharray="6 4"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8 }}
            />
          </motion.g>
        )}
      </svg>

      {/* TMS card */}
      <AnimatePresence>
        {showTmsLink && (
          <motion.div
            className="absolute right-2 top-4 bg-white/95 border border-border rounded-lg shadow-lg p-3 w-44"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded bg-petrol/10 flex items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--petrol))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              <span className="text-xs font-heading font-semibold text-foreground">TMS-integration</span>
            </div>
            <div className="space-y-1.5 text-[11px] font-body">
              {showIncoming && (
                <motion.div className="flex items-center gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                  <span className="text-muted-foreground">Ny leveransförfrågan…</span>
                </motion.div>
              )}
              {showResponse && (
                <motion.div className="flex items-center gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                  <span className="text-foreground font-medium">Zon 2 → 99 kr ✓</span>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
